
import React from "react";
import { Calendar, Clock, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ClassType } from "@/lib/supabase";
import { format, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";

// Array of fallback images that were provided
const fallbackImages = [
  "/lovable-uploads/6238269c-1d4e-47d3-9b7f-9898605261ea.png", 
  "/lovable-uploads/81f8fc30-883f-408e-bbb6-f0670bb4ee55.png", 
  "/lovable-uploads/6d4be08b-3682-4b4b-afb9-1a4a24f53b1c.png", 
  "/lovable-uploads/fc11d0b3-17c0-43d9-9e7b-70cac7cdf6b5.png", 
  "/lovable-uploads/e1b667cd-e6a9-4927-ae11-64badba95792.png", 
  "/lovable-uploads/ab3f3936-6d24-42f0-9930-09df6906158e.png", 
  "/lovable-uploads/f76dbc95-94b3-47ae-b78c-d0ff6d63643b.png", 
  "/lovable-uploads/b1b5fa43-0204-4d07-9524-b21b42bb4a96.png"
];

interface ClassCardProps {
  classData: ClassType;
  index?: number;
}

export const ClassCard: React.FC<ClassCardProps> = ({ classData, index = 0 }) => {
  const { t } = useTranslation();
  const { id, day, date, time, duration, capacity, availableSpots, image } = classData;
  
  const isAvailable = availableSpots > 0;
  const availabilityPercent = Math.floor((availableSpots / capacity) * 100);
  
  // Format the date if it exists
  const formattedDate = date ? format(parseISO(date), 'MMM d, yyyy') : day;
  
  // Get a fallback image based on the class index modulo the number of fallback images
  const getFallbackImage = () => {
    const imageIndex = index % fallbackImages.length;
    return fallbackImages[imageIndex];
  };
  
  // Use the provided image if it exists, otherwise use a fallback image
  const displayImage = image && image.trim() !== "" ? image : getFallbackImage();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="subtle-scale group rounded-xl overflow-hidden border border-border bg-card"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={displayImage}
          alt={`${t('classes.title')} ${formattedDate}`}
          className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
          onError={(e) => {
            // If the image fails to load, try a fallback
            const target = e.target as HTMLImageElement;
            if (target.src !== getFallbackImage()) {
              target.src = getFallbackImage();
            }
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute bottom-4 left-4 bg-background/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" />
          <span>{duration}</span>
        </div>
        <div className="absolute bottom-4 right-4 bg-background/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1.5">
          <Calendar className="w-3.5 h-3.5" />
          <span>{day}</span>
        </div>
      </div>
      
      <div className="p-5">
        <div className="mb-4">
          <h3 className="text-xl font-display font-medium text-foreground mb-1">{formattedDate}</h3>
          <div className="flex items-center text-sm text-muted-foreground gap-3">
            <div className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              <span>{time}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Users className="w-4 h-4" />
              <span>
                {t('classes.details.availableSpots', {
                  available: availableSpots,
                  capacity: capacity
                })}
              </span>
            </div>
          </div>
        </div>
        
        <div className="mb-4">
          <div className="flex justify-between text-xs mb-1.5">
            <span>{t('classes.details.available')}</span>
            <span className={availabilityPercent < 20 ? "text-destructive" : "text-primary"}>
              {availabilityPercent}%
            </span>
          </div>
          <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full ${
                availabilityPercent < 20 ? "bg-destructive" : "bg-primary"
              }`}
              style={{ width: `${availabilityPercent}%` }}
            />
          </div>
        </div>
        
        <Link to={`/booking?classId=${id}`}>
          <Button 
            className="w-full" 
            variant={isAvailable ? "default" : "outline"}
            disabled={!isAvailable}
          >
            {isAvailable ? t('classes.details.book') : t('booking.form.title')}
          </Button>
        </Link>
      </div>
    </motion.div>
  );
};
